import React, { useEffect, useState } from 'react';
import { formatDateLocaleLima } from '../../utils/dateUtils';

const pendingLinesCount = (req) =>
  (req.items || []).filter((ri) => parseFloat(ri.pending_quantity ?? ri.quantity ?? 0) > 0.0001).length;

/**
 * Modal para anidar requerimientos (cantidades pendientes → ítems de OC / documento).
 */
const RequirementSelectorModal = ({
  isOpen,
  onClose,
  requirements = [],
  linkedRequirementIds = [],
  onConfirm,
  loading = false,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setSelectedIds([]);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const toggle = (id) => {
    const sid = String(id);
    setSelectedIds((prev) =>
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid],
    );
  };

  const handleConfirm = () => {
    if (!selectedIds.length) return;
    onConfirm(selectedIds.map((id) => parseInt(id, 10)));
  };

  const linkedSet = new Set((linkedRequirementIds || []).map(String));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10 rounded-t-xl">
          <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
            <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            Anidar requerimientos
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600 mb-4">
            Seleccione uno o más requerimientos emitidos con cantidades pendientes. Se cargarán productos y cantidades
            (sin precios); complete los precios del proveedor en la orden.
          </p>

          {linkedSet.size > 0 && (
            <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <p className="text-xs font-medium text-teal-900 mb-1">Ya vinculados en esta orden</p>
              <p className="text-sm text-teal-800">
                {requirements
                  .filter((r) => linkedSet.has(String(r.id)))
                  .map((r) => r.code)
                  .join(', ') || `${linkedSet.size} requerimiento(s)`}
              </p>
            </div>
          )}

          {loading ? (
            <p className="text-center text-gray-500 py-8">Cargando requerimientos...</p>
          ) : requirements.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No hay requerimientos con ítems pendientes</p>
          ) : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto border border-gray-200 rounded-lg p-3">
              {requirements.map((req) => {
                const sid = String(req.id);
                const isSelected = selectedIds.includes(sid);
                const pct = req.fulfillment_percent ?? 0;
                const lines = pendingLinesCount(req);
                const disabled = lines === 0;

                return (
                  <label
                    key={req.id}
                    className={`flex items-start gap-3 p-3 border rounded-lg transition-colors ${
                      disabled
                        ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200'
                        : isSelected
                        ? 'bg-teal-50 border-teal-300 cursor-pointer'
                        : 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={disabled}
                      onChange={() => !disabled && toggle(req.id)}
                      className="mt-1 w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-mono font-medium text-teal-800">{req.code}</span>
                        <span className="text-xs text-gray-500">
                          {formatDateLocaleLima(req.request_date)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {lines} línea(s) pendiente(s) · {pct}% atendido
                      </div>
                      {req.needed_by_date && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          Requerido para: {formatDateLocaleLima(req.needed_by_date)}
                        </div>
                      )}
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-[8rem]">
                          <div
                            className="bg-teal-600 h-1.5 rounded-full"
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">{pct}%</span>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white font-medium"
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedIds.length || loading}
            className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cargar ítems seleccionados
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequirementSelectorModal;
